import React, { useEffect, useState } from 'react';
import './transferList.scss';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Search from '@material-ui/icons/Search';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
// eslint-disable-next-line no-unused-vars
import { TransferListProps, TransferListItem, TransferListResult } from './typing';

const ListEnum = Object.freeze({
    Left: 'Left',
    Right: 'Right',
});

/**
 * @param {TransferListItem[]} a
 * @param {TransferListItem[]} b
 * @returns
 */
function not(a, b) {
    return a.filter((value) => b.indexOf(value) === -1);
}

/**
 * @param {TransferListItem[]} a
 * @param {TransferListItem[]} b
 * @returns
 */
function intersection(a, b) {
    return a.filter((value) => b.indexOf(value) !== -1);
}

/**
 * @param {TransferListItem[]} a
 * @param {TransferListItem[]} b
 * @returns
 */
function union(a, b) {
    return [...a, ...not(b, a)];
}

/**
 * @param {TransferListProps} props
 * @returns
 */
export default function TransferList({
    width = 300,
    items,
    disabled,
    onChange,
    useTotalLeft,
    onItemToggle = () => { },
    useSearchBars,
    useSelectAll,
    useTotalRight,
    titleLeft,
    titleRight,
}) {
    /** @type {[ checked: TransferListItem[], setChecked: Function]} */
    const [checked, setChecked] = useState([]);

    /** @type {[ left: TransferListItem[], setLeft: Function]} */
    const [left, setLeft] = useState(items.filter((x) => x.selected === false));

    /** @type {[ right: TransferListItem[], setRight: Function]} */
    const [right, setRight] = useState(items.filter((x) => x.selected === true));

    const [searchValueLeft, setSearchValueLeft] = useState('');
    const [searchValueRight, setSearchValueRight] = useState('');

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);
    useEffect(() => {
        setLeft(items.filter((x) => x.selected === false));
        setRight(items.filter((x) => x.selected === true));
    }, [JSON.stringify(items)]);

    const numberOfChecked = (listItems) => intersection(checked, listItems).length;

    const handleToggleAll = (listItems) => () => {
        if (numberOfChecked(listItems) === listItems.length) {
            setChecked(not(checked, listItems));
        } else {
            setChecked(union(checked, listItems));
        }
    };

    /**
     * @param {TransferListItem[]} leftSelection
     * @param {TransferListItem[]} rightSelection
     */
    const handleChange = (leftSelection, rightSelection) => {
        if (typeof onChange === 'function') {
            /** @type {TransferListResult} */
            const result = {
                unselected: leftSelection.map((x) => x.id),
                selected: rightSelection.map((x) => x.id),
            };

            onChange(result);
        }
    };

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        if (onItemToggle) {
            onItemToggle(value);
        }

        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        const leftSelection = not(left, leftChecked);
        const rightSelection = right.concat(leftChecked);
        setRight(rightSelection);
        setLeft(leftSelection);
        setChecked(not(checked, leftChecked));
        handleChange(leftSelection, rightSelection);
    };

    const handleCheckedLeft = () => {
        const leftSelection = left.concat(rightChecked);
        const rightSelection = not(right, rightChecked);

        setLeft(leftSelection);
        setRight(rightSelection);
        setChecked(not(checked, rightChecked));
        handleChange(leftSelection, rightSelection);
    };

    const customList = (listItems, search) => {
        const itemsWithSearch = listItems.map((item) => {
            const isFoundInSearch = item.label.toLowerCase().includes(search.toLowerCase());
            return { isFoundInSearch, item };
        });

        itemsWithSearch.sort((x, y) => {
            if (x.isFoundInSearch && !y.isFoundInSearch) {
                return -1;
            } if (!x.isFoundInSearch && y.isFoundInSearch) {
                return 1;
            }
            return x.item.label.localeCompare(y.item.label);
        });

        return (
            <List
                dense
                component="div"
                role="list"
                className="transferlist_transferList"
                style={{ width }}
            >
                {itemsWithSearch.map((element) => {
                    const { isFoundInSearch, item } = element;
                    const labelId = `transfer-list-item-${item.id}-label`;
                    return (
                        <ListItem
                            key={`list_item_transfer_${item.id}`}
                            className="transferlist_listItemElement"
                            role="listitem"
                            button
                            onClick={handleToggle(item)}
                            disabled={disabled || item.disabled}
                        >
                            <ListItemIcon>
                                <Checkbox
                                    color="primary"
                                    checked={checked.indexOf(item) !== -1}
                                    disabled={disabled || item.disabled}
                                    tabIndex={-1}
                                    disableRipple
                                    inputProps={{ 'aria-labelledby': labelId }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id={labelId}
                                className={isFoundInSearch ? 'transferlist_itemFoundInSearch' : 'transferlist_itemNotFoundInSearch'}
                                primary={item.label}
                                secondary={item.secondaryLabel}
                            />
                        </ListItem>
                    );
                })}
                <ListItem />
            </List>
        );
    };

    /**
     * @param {TransferListItem[]} listItems
     * @param {string} leftRight
     * @returns
     */
    const drawList = (listItems, leftRight) => {
        const searchValue = leftRight === ListEnum.Left ? searchValueLeft : searchValueRight;
        const searchValueHandler = leftRight === ListEnum.Left ? setSearchValueLeft : setSearchValueRight;

        const useTotal = leftRight === ListEnum.Left ? useTotalLeft : useTotalRight;
        const title = leftRight === ListEnum.Left ? titleLeft : titleRight;

        return (
            <Paper className="transferlist_paper">
                <div className="transferlist_listHeader">
                    <div className="transferlist_headerTitle">
                        {useTotal ? listItems.length : null}
                        {` ${title}`}
                    </div>
                    {(useSearchBars || useSelectAll)
                        ? (
                            <div className="transferlist_headerActions">
                                {
                                    useSelectAll
                                        ? (
                                            <div className="transferlist_checkAll">
                                                <Checkbox
                                                    color="primary"
                                                    onClick={handleToggleAll(listItems)}
                                                    checked={numberOfChecked(listItems) === listItems.length && listItems.length !== 0}
                                                    indeterminate={numberOfChecked(listItems) !== listItems.length
                                                        && numberOfChecked(listItems) !== 0}
                                                    disabled={listItems.length === 0 || disabled}
                                                    inputProps={{ 'aria-label': 'all items selected' }}
                                                />
                                            </div>
                                        )
                                        : null
                                }
                                {
                                    useSearchBars
                                        ? (
                                            <div className="transferlist_headerSearch">
                                                <TextField
                                                    id="react-search-input"
                                                    placeholder="Search..."
                                                    value={searchValue}
                                                    onChange={(e) => searchValueHandler(e.target.value)}
                                                    InputProps={{
                                                        startAdornment:
                                                            (
                                                                <InputAdornment position="start">
                                                                    <Search />
                                                                </InputAdornment>
                                                            ),
                                                    }}
                                                />
                                            </div>
                                        )
                                        : null
                                }
                            </div>
                        )
                        : null}
                </div>
                {customList(listItems, searchValue)}
            </Paper>
        );
    };

    return (
        <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            className="transferlist_root"
        >
            <Grid item>{drawList(left, ListEnum.Left)}</Grid>
            <Grid item>
                <Grid
                    container
                    direction="column"
                    alignItems="center"
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        className="transferlist_button"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected right"
                    >
                        <ChevronRightIcon />
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        className="transferlist_button"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected left"
                    >
                        <ChevronLeftIcon />
                    </Button>
                </Grid>
            </Grid>
            <Grid item>{drawList(right, ListEnum.Right)}</Grid>
        </Grid>
    );
}
